import React, { useEffect, useState } from 'react';
import { assetsAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Laptop, UserPlus, Undo2, Trash2 } from 'lucide-react';

const CATEGORIES = ['laptop', 'mobile', 'sim', 'id_card', 'monitor', 'accessory', 'tablet', 'other'];
const STATUS_COLORS = { available: 'default', assigned: 'secondary', under_repair: 'destructive', lost: 'destructive', decommissioned: 'outline' };

const Assets = () => {
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [returnDialog, setReturnDialog] = useState(null);
  const [newAsset, setNewAsset] = useState({ asset_tag: '', name: '', category: 'laptop', brand: '', serial_number: '', purchase_cost: '' });
  const [assignTo, setAssignTo] = useState('');
  const [returnCondition, setReturnCondition] = useState('good');
  const [returnNotes, setReturnNotes] = useState('');
  const [acting, setActing] = useState(false);

  const fetchAll = async () => {
    try {
      const [a, e] = await Promise.all([assetsAPI.list(), employeesAPI.getAll()]);
      setAssets(a.data);
      setEmployees(e.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const createAsset = async () => {
    if (!newAsset.asset_tag || !newAsset.name) { toast.error('Tag and name required'); return; }
    setActing(true);
    try {
      await assetsAPI.create({ ...newAsset, purchase_cost: newAsset.purchase_cost ? parseFloat(newAsset.purchase_cost) : null });
      toast.success('Asset created');
      setCreateOpen(false);
      setNewAsset({ asset_tag: '', name: '', category: 'laptop', brand: '', serial_number: '', purchase_cost: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const assign = async () => {
    if (!assignDialog || !assignTo) { toast.error('Select employee'); return; }
    setActing(true);
    try {
      await assetsAPI.assign(assignDialog.id, { employee_id: assignTo });
      toast.success('Assigned');
      setAssignDialog(null);
      setAssignTo('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const doReturn = async () => {
    if (!returnDialog) return;
    setActing(true);
    try {
      await assetsAPI.return(returnDialog.id, { condition: returnCondition, return_notes: returnNotes });
      toast.success('Returned');
      setReturnDialog(null);
      setReturnNotes('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this asset permanently?')) return;
    try { await assetsAPI.delete(id); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const summary = assets.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Laptop />Asset Management</h1>
          <p className="text-muted-foreground">Track laptops, mobiles, SIM cards, ID cards and other assets</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild><Button data-testid="add-asset-btn"><Plus size={16} className="mr-2" />Add Asset</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Asset</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Asset Tag *</Label><Input value={newAsset.asset_tag} onChange={(e) => setNewAsset({...newAsset, asset_tag: e.target.value})} data-testid="asset-tag" placeholder="LAP-001" /></div>
                <div><Label>Category</Label>
                  <Select value={newAsset.category} onValueChange={(v) => setNewAsset({...newAsset, category: v})}>
                    <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Name *</Label><Input value={newAsset.name} onChange={(e) => setNewAsset({...newAsset, name: e.target.value})} data-testid="asset-name" placeholder="MacBook Pro 14" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Brand</Label><Input value={newAsset.brand} onChange={(e) => setNewAsset({...newAsset, brand: e.target.value})} /></div>
                <div><Label>Serial #</Label><Input value={newAsset.serial_number} onChange={(e) => setNewAsset({...newAsset, serial_number: e.target.value})} /></div>
              </div>
              <div><Label>Purchase Cost</Label><Input type="number" value={newAsset.purchase_cost} onChange={(e) => setNewAsset({...newAsset, purchase_cost: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={createAsset} disabled={acting} data-testid="save-asset-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{assets.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Available</p><p className="text-2xl font-bold text-emerald-600">{summary.available || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Assigned</p><p className="text-2xl font-bold text-blue-600">{summary.assigned || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Repair</p><p className="text-2xl font-bold text-amber-600">{summary.under_repair || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Lost</p><p className="text-2xl font-bold text-red-600">{summary.lost || 0}</p></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tag</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
            <TableHead>Brand</TableHead><TableHead>Status</TableHead><TableHead>Assigned To</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {assets.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No assets yet</TableCell></TableRow>}
            {assets.map(a => (
              <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                <TableCell className="font-mono">{a.asset_tag}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell><Badge variant="outline">{a.category}</Badge></TableCell>
                <TableCell>{a.brand || '—'}</TableCell>
                <TableCell><Badge variant={STATUS_COLORS[a.status]}>{a.status.replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell>{a.assigned_to_name || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {a.status === 'available' && <Button size="sm" variant="outline" onClick={() => setAssignDialog(a)} data-testid={`assign-${a.id}`}><UserPlus size={14} /></Button>}
                    {a.status === 'assigned' && <Button size="sm" variant="outline" onClick={() => setReturnDialog(a)} data-testid={`return-${a.id}`}><Undo2 size={14} /></Button>}
                    <Button size="sm" variant="outline" onClick={() => remove(a.id)} data-testid={`del-${a.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign {assignDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Employee</Label>
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button onClick={assign} disabled={acting} data-testid="confirm-assign-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!returnDialog} onOpenChange={(o) => !o && setReturnDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Return {returnDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Condition</Label>
              <Select value={returnCondition} onValueChange={setReturnCondition}>
                <SelectTrigger data-testid="return-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good (back to Available)</SelectItem>
                  <SelectItem value="damaged">Damaged (to Repair)</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={returnNotes} onChange={(e) => setReturnNotes(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialog(null)}>Cancel</Button>
            <Button onClick={doReturn} disabled={acting} data-testid="confirm-return-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assets;
